import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getLocaleLanguage } from 'modules/i18n';
import { useAccessToken } from 'utils/auth';
import {
  clearCVFormData,
  CVBuilderFormValues,
  loadCVFormData,
  saveCVFormData
} from 'utils/cv-form-db';
import { logError } from 'utils/datadog';
import { CV_BUILDER_STEPS, defaultFormValues } from '../constants';
import {
  useCreateCvMutation,
  useTalentCvQuery,
  useUpdateCvMutation
} from '../query';
import { fromCvDto, toCreateCvRequest } from '../toCreateCvRequest';
import { CvBuilderData, CvBuilderDataContext } from './CvBuilderDataContext';

/**
 * CvBuilderData implementation for the logged-in talent: Auth0 token, the local
 * IndexedDB draft, and the authenticated `/curriculumVitaes` endpoints. This is the
 * EXACT behavior the wizard had before the port was introduced — the init/seed gate
 * and create-vs-update submit were lifted out of CVBuilder/Summary unchanged.
 */
export const AuthenticatedCvDataProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const { token, isLoading: tokenLoading } = useAccessToken();
  const { isAuthenticated } = useAuth0();
  const cvQuery = useTalentCvQuery(token);
  const createMutation = useCreateCvMutation();
  const updateMutation = useUpdateCvMutation();
  const [initialValues, setInitialValues] =
    useState<CVBuilderFormValues | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // Persist outcome, surfaced by SavedIndicator. IndexedDB rarely fails, but when it does
  // (private mode, quota, a locked DB) the draft silently isn't there on return — so the
  // failure is worth showing rather than only logging.
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const initializedRef = useRef(false);
  // Synchronous re-entrancy guards (immune to the disabled-button render gap): a
  // double-click must not fire submit() twice — the second create() races to the
  // backend's ERROR_CV_ALREADY_EXISTS. hasSubmittedRef latches the terminal state so
  // a late retry can't re-submit after success.
  const submitInFlightRef = useRef(false);
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    // Wait for Auth0 to settle one way or the other.
    if (tokenLoading) return;
    // Auth0 settled with an authenticated user but useAccessToken's
    // getAccessTokenSilently() is still in flight — token will arrive
    // shortly. Don't initialize from IndexedDB yet; we'd miss the server CV.
    if (isAuthenticated && !token) return;
    // Logged-in talent: wait for the CV query to finish at least one fetch.
    // No token (e.g. anonymous or test environment): proceed straight to the
    // IndexedDB / defaults fallback so the wizard still renders.
    if (token && !cvQuery.isFetched) return;
    initializedRef.current = true;

    const fetchData = async () => {
      try {
        const currentLanguage = getLocaleLanguage();
        const serverCv = cvQuery.data?.data;

        if (serverCv) {
          setInitialValues({
            ...defaultFormValues,
            ...fromCvDto(serverCv),
            language: currentLanguage as CVBuilderFormValues['language']
          });
          return;
        }

        const saved = await loadCVFormData();
        setInitialValues({
          ...defaultFormValues,
          ...saved,
          language: currentLanguage as CVBuilderFormValues['language']
        });
      } catch (error) {
        logError('Error loading CV form data', error);
        navigate(`?step=${CV_BUILDER_STEPS.LANGUAGE}`);
      }
    };
    void fetchData();
  }, [
    navigate,
    tokenLoading,
    isAuthenticated,
    token,
    cvQuery.isFetched,
    cvQuery.data
  ]);

  const value = useMemo<CvBuilderData>(
    () => ({
      initialValues,
      // Fire-and-forget contract: autosave must never reject (the wizard navigates
      // without awaiting it). IndexedDB rarely fails; if it does, log and move on.
      saveDraft: async (values: CVBuilderFormValues) => {
        try {
          await saveCVFormData(values);
          setLastSavedAt(Date.now());
          setSaveFailed(false);
        } catch (error) {
          setSaveFailed(true);
          logError('CV autosave to IndexedDB failed', error);
        }
      },
      lastSavedAt,
      saveFailed,
      loadDraft: loadCVFormData,
      clearDraft: clearCVFormData,
      submit: async (values: CVBuilderFormValues) => {
        if (submitInFlightRef.current || hasSubmittedRef.current) {
          return; // re-entrancy guard — synchronous, beats the disabled-button render gap
        }
        submitInFlightRef.current = true;
        try {
          const payload = toCreateCvRequest(values);
          if (cvQuery.data) {
            await updateMutation.mutateAsync(payload);
          } else {
            await createMutation.mutateAsync(payload);
          }
          await clearCVFormData();
          hasSubmittedRef.current = true; // latch before the terminal swap
          setSubmitted(true); // wizard swaps to the confirmation screen
        } catch (error) {
          submitInFlightRef.current = false; // allow a retry
          throw error; // Summary surfaces a submit-error notification
        }
      },
      isSubmitting: createMutation.isLoading || updateMutation.isLoading,
      submitted,
      // A server CV was returned → the wizard lands on the Summary (view + edit) instead
      // of the first create step. `!!` collapses the undefined-until-fetched query state.
      hasExistingCv: !!cvQuery.data?.data
    }),
    [
      initialValues,
      cvQuery.data,
      createMutation,
      updateMutation,
      submitted,
      lastSavedAt,
      saveFailed
    ]
  );

  return (
    <CvBuilderDataContext.Provider value={value}>
      {children}
    </CvBuilderDataContext.Provider>
  );
};
